import React, { useState, useEffect } from 'react';
import Header from '../../Header';
import './StripeAdmin.scss';

const StripeAdmin = () => {
    const [subscriptions, setSubscriptions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [activeTab, setActiveTab] = useState('active');
    const [totals, setTotals] = useState({
        monthlyTotal: 0,
        annualTotal: 0,
        activeSubscriptions: 0
    });

    const isActiveSubscription = (sub) => sub.status === 'active';
    const activeList = subscriptions.filter(isActiveSubscription);
    const cancelledList = subscriptions.filter(sub => !isActiveSubscription(sub));
    const visibleSubscriptions = activeTab === 'active' ? activeList : cancelledList;

    useEffect(() => {
        const fetchSubscriptions = async () => {
            try {
                const token = localStorage.getItem('auth_token');
                if (!token) {
                    throw new Error('Authentication token not found');
                }

                const response = await fetch(`${process.env.REACT_APP_API_URL}/admin/get_subscriptions.php`, {
                    headers: {
                        'Authorization': `Bearer ${token}`
                    }
                });

                if (!response.ok) {
                    throw new Error('Failed to fetch subscriptions');
                }

                const data = await response.json();
                if (!data.success) {
                    throw new Error(data.message || 'Failed to fetch subscriptions');
                }

                // One row per Stripe subscription. Concurrent webhooks can store
                // the same sub_ id more than once; keep the first (newest) row.
                const seenStripeIds = new Set();
                const uniqueSubscriptions = data.subscriptions.filter(sub => {
                    const key = sub.stripe_subscription_id || `row-${sub.id}`;
                    if (seenStripeIds.has(key)) {
                        return false;
                    }
                    seenStripeIds.add(key);
                    return true;
                });

                // Format the subscription data
                const formattedSubscriptions = uniqueSubscriptions.map(sub => ({
                    ...sub,
                    amount: parseFloat(sub.amount), // Convert string amount to number
                    amount_formatted: `£${parseFloat(sub.amount).toFixed(2)}` // Format amount properly
                }));

                setSubscriptions(formattedSubscriptions);
                
                // Calculate totals using the numeric amounts
                const activeSubs = formattedSubscriptions.filter(sub => sub.status === 'active');
                const monthlyAmount = activeSubs
                    .filter(sub => sub.subscription_type === 'monthly')
                    .reduce((total, sub) => total + sub.amount, 0);
                const annualAmount = activeSubs
                    .filter(sub => sub.subscription_type === 'annual' || sub.subscription_type === 'yearly')
                    .reduce((total, sub) => total + sub.amount, 0);

                setTotals({
                    monthlyTotal: monthlyAmount,
                    annualTotal: annualAmount,
                    activeSubscriptions: activeSubs.length
                });

            } catch (err) {
                setError(err.message);
            } finally {
                setLoading(false);
            }
        };

        fetchSubscriptions();
    }, []);

    if (loading) {
        return (
            <div className="StripeAdmin container">
                <Header />
                <div className="loading">Loading subscriptions...</div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="StripeAdmin container">
                <Header />
                <div className="error">{error}</div>
            </div>
        );
    }

    return (
        <div className="StripeAdmin container">
            <Header />
            <h1>Pro subscriptions</h1>
            <p className="page-note">
                First Whistle Pro only. Club monthly fees, such as SLJFC, are not included.
            </p>
            
            <div className="summary-section">
                <div className="totals">
                    <div className="total-item">
                        <div className="label">Monthly Revenue</div>
                        <div className="value">£{totals.monthlyTotal.toFixed(2)}</div>
                    </div>
                    <div className="total-item">
                        <div className="label">Annual Revenue</div>
                        <div className="value">£{totals.annualTotal.toFixed(2)}</div>
                    </div>
                    <div className="total-item">
                        <div className="label">Active Subscriptions</div>
                        <div className="value">{totals.activeSubscriptions}</div>
                    </div>
                </div>
            </div>

            <div className="tabs">
                <button
                    type="button"
                    className={activeTab === 'active' ? 'active' : ''}
                    onClick={() => setActiveTab('active')}
                >
                    Active
                    <span className="tab-count">{activeList.length}</span>
                </button>
                <button
                    type="button"
                    className={`cancelled-tab ${activeTab === 'cancelled' ? 'active' : ''}`}
                    onClick={() => setActiveTab('cancelled')}
                >
                    Cancelled
                    <span className="tab-count">{cancelledList.length}</span>
                </button>
            </div>

            {visibleSubscriptions.length === 0 ? (
                <div className="empty-list">
                    {activeTab === 'active'
                        ? 'No active subscriptions.'
                        : 'No cancelled subscriptions.'}
                </div>
            ) : (
                <table>
                    <thead>
                        <tr>
                            <th>Account</th>
                            <th>Team</th>
                            <th>Status</th>
                            <th>Amount</th>
                            <th>Type</th>
                            <th>Start Date</th>
                            <th>End Date</th>
                        </tr>
                    </thead>
                    <tbody>
                        {visibleSubscriptions.map((subscription, index) => (
                            <tr key={`${subscription.id}-${index}`}>
                                <td className="player-cell">
                                    <div className="player-name">{subscription.player_name}</div>
                                    <div className="player-email">{subscription.email}</div>
                                    <div className="account-type">{subscription.account_type || (subscription.subscriber_type === 'parent' ? 'Parent Pro' : 'Player Pro')}</div>
                                </td>
                                <td className="team-cell">
                                    <div className="team-name">{subscription.team_name}</div>
                                    <div className="team-type">{subscription.team_type}</div>
                                </td>
                                <td className="status-cell">
                                    <span className={`status ${subscription.status}`}>
                                        {subscription.status}
                                    </span>
                                </td>
                                <td className="amount-cell">{subscription.amount_formatted}</td>
                                <td>{subscription.subscription_type === 'annual' || subscription.subscription_type === 'yearly' ? 'Annual' : 'Monthly'}</td>
                                <td className="date-cell">{subscription.start_date}</td>
                                <td className="date-cell">{subscription.end_date || '-'}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            )}
        </div>
    );
};

export default StripeAdmin; 